import { memo, ReactElement, useEffect, useState } from 'react';
import PlacesAutocomplete from 'react-places-autocomplete';
import cx from 'classnames';
import {
  FieldError,
  UseFormClearErrors,
  UseFormSetValue,
  UseFormTrigger,
} from 'react-hook-form';
import { isEmpty, isFunction, isNil, map } from 'lodash';

import {
  IGoogleData,
  useGooglePlace,
} from 'components/google-places/useGooglePlaces';
import { GoogleAutocompleteWrapper } from './googleAutocomplete.styled';
import { Names } from 'types/fields';
import { TextFieldLabel } from 'components/common/text-field';
import Typography from 'components/common/typography';

function GoogleAutocomplete({
  forBuy,
  placeholder,
  mode,
  setValue,
  error,
  clearErrors,
  name,
  setGoogleData,
  trigger,
  label,
  defaultValue,
}: GoogleAutocompleteProps): ReactElement {
  const [isTouched, setIsTouched] = useState(false);
  const {
    input,
    hanldeChange,
    hanldeSelect,
    selOptions,
    buyOptions,
    googleData,
    setGoogleData: setGoogleHookData,
  } = useGooglePlace();

  useEffect(() => {
    setGoogleHookData({ full: input });
    setValue('address', input);

    if (!isNil(input)) {
      clearErrors('address');
    }
  }, [clearErrors, input, setGoogleHookData, setValue]);

  useEffect(() => {
    if (!isEmpty(defaultValue) && !isNil(defaultValue)) {
      setGoogleHookData({ full: defaultValue });
      hanldeChange(defaultValue);
      setValue('address', defaultValue);
    }
  }, [defaultValue, hanldeChange, setGoogleHookData, setValue]);

  useEffect(() => {
    if (isTouched) {
      trigger('address');
    }
  }, [isTouched, trigger]);

  useEffect(() => {
    if (!isNil(googleData) && isFunction(setGoogleData)) {
      setGoogleData(googleData);
    }
  }, [googleData, setGoogleData]);

  function handleInputBlur(
    placesAutocompleteBlurHandler: (...args: any[]) => void
  ) {
    return function onBlur() {
      placesAutocompleteBlurHandler();
      setIsTouched(true);
    };
  }

  return (
    <GoogleAutocompleteWrapper
      className={cx('places', { 'variant-1': mode === 'variant-1' })}
      hasLabel={Boolean(label)}
    >
      {label && (
        <TextFieldLabel
          data-testid="text-field-label"
          className="outlined-input-label"
        >
          <label htmlFor={name}>
            <Typography fontWeight="medium">{label}</Typography>
          </label>
        </TextFieldLabel>
      )}
      <div className="places-container">
        <PlacesAutocomplete
          value={input}
          onChange={hanldeChange}
          onSelect={hanldeSelect}
          searchOptions={forBuy ? buyOptions.current : selOptions.current}
        >
          {({
            getInputProps,
            suggestions,
            getSuggestionItemProps,
            loading,
          }) => (
            <div className="autocomplete">
              <input
                placeholder={`${placeholder || `Address`}`}
                className={cx({
                  invalid: Boolean(error),
                  'autocomplete-input': true,
                })}
                id={name}
                {...getInputProps()}
                onBlur={handleInputBlur(getInputProps().onBlur)}
              />
              <div className="autocomplete-text-field-error">
                {error?.message && <p>{error.message}</p>}
              </div>
              <div className="autocomplete-dropdown">
                {loading && <div>Loading...</div>}
                {map(suggestions, (suggestion, idx) => (
                  <div {...getSuggestionItemProps(suggestion)} key={idx}>
                    <span>{suggestion.description}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </PlacesAutocomplete>
      </div>
    </GoogleAutocompleteWrapper>
  );
}

type GoogleAutocompleteProps = {
  forBuy?: boolean;
  placeholder?: string;
  forIframe?: boolean;
  note?: string;
  mode?: 'variant-1';
  withArrow?: boolean;
  redirectUrl?: string;
  isAuth?: string;
  error?: FieldError;
  clearErrors: UseFormClearErrors<any>;
  setValue: UseFormSetValue<any>;
  name: Names;
  setGoogleData?: (googleData: IGoogleData & { full: string }) => void;
  trigger: UseFormTrigger<any>;
  label?: string;
  defaultValue?: string;
};

export default memo(GoogleAutocomplete);
